import { motion } from "framer-motion";
import { Palette, Rocket, Bot, Check } from "lucide-react";
import PageHero from "@/components/PageHero";
import CTABand from "@/components/CTABand";

const divisions = [
  {
    key: "brand",
    title: "Brand",
    icon: Palette,
    tagline: "Identity, narrative, design language.",
    color: "from-neon-orange to-neon-magenta",
    services: [
      { name: "Brand Strategy", desc: "Positioning, messaging frameworks, audience architecture." },
      { name: "Brand Identity", desc: "Logo systems, type, color, motion identity." },
      { name: "Creative Direction", desc: "Art direction for campaigns, launches, and content." },
      { name: "Content Strategy", desc: "Editorial pillars, narrative arcs, distribution maps." },
      { name: "Video Production", desc: "Brand films, product films, social-native video." },
      { name: "UI/UX Design", desc: "Interface design that converts and feels premium." },
      { name: "Website Design", desc: "High-craft sites engineered for performance and growth." },
      { name: "Brand Systems", desc: "Design systems and guidelines for repeatable craft." },
    ],
  },
  {
    key: "growth",
    title: "Growth",
    icon: Rocket,
    tagline: "Demand engines. Lead velocity. Pipeline.",
    color: "from-neon-magenta to-neon-purple",
    services: [
      { name: "SEO", desc: "Topical authority, technical SEO, programmatic content." },
      { name: "GEO", desc: "Generative engine optimization for AI search surfaces." },
      { name: "Performance Marketing", desc: "Paid acquisition across Meta, Google, LinkedIn, YouTube." },
      { name: "Lead Generation", desc: "Always-on multi-channel capture funnels." },
      { name: "Conversion Optimization", desc: "CRO programs across funnel, copy and UX." },
      { name: "Growth Campaigns", desc: "Launches, activations, demand bursts." },
      { name: "Analytics", desc: "Attribution, dashboards, experimentation infrastructure." },
      { name: "Marketing Infrastructure", desc: "Tooling, tagging, lifecycle and CDP setup." },
    ],
  },
  {
    key: "automation",
    title: "Automation",
    icon: Bot,
    tagline: "AI agents. CRM systems. Compounding ops.",
    color: "from-neon-cyan to-neon-blue",
    services: [
      { name: "CRM Systems", desc: "From zero to a clean, automated pipeline." },
      { name: "AI Agents", desc: "Autonomous workers for sales, ops and support." },
      { name: "Chatbots", desc: "Conversational layers for capture and qualification." },
      { name: "Workflow Automation", desc: "Connect every tool with reliable orchestration." },
      { name: "Internal Tools", desc: "Lightweight web apps your team actually uses." },
      { name: "Sales Automation", desc: "Sequences, enrichment, scoring and routing." },
      { name: "Business Automation", desc: "Eliminate manual work across operations." },
      { name: "Custom AI Systems", desc: "Bespoke models, RAG, copilots — built for you." },
    ],
  },
];

export default function Services() {
  return (
    <div data-testid="services-root">
      <PageHero
        eyebrow="Services"
        title={<>Three divisions. <span className="text-gradient-warm">One growth system.</span></>}
        subtitle="Brand, Growth and Automation are designed to operate independently — and amplify each other when combined. Pick a division or run the full stack."
        accent="warm"
      />

      <div className="container-x space-y-24 pb-20">
        {divisions.map((d, idx) => {
          const Icon = d.icon;
          return (
            <section
              key={d.key}
              id={d.key}
              data-testid={`services-${d.key}`}
              className="relative"
            >
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                <div className="lg:col-span-4 lg:sticky lg:top-28 self-start">
                  <div className={`inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br ${d.color}`}>
                    <Icon size={22} />
                  </div>
                  <h2 className="mt-6 text-4xl sm:text-5xl font-extrabold tracking-tight uppercase">{d.title}</h2>
                  <p className="mt-3 text-white/65">{d.tagline}</p>
                  <p className="mt-6 font-mono text-[11px] tracking-[0.18em] uppercase text-white/40">
                    Division 0{idx + 1}
                  </p>
                </div>

                <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {d.services.map((s, i) => (
                    <motion.div
                      key={s.name}
                      initial={{ opacity: 0, y: 18 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, margin: "-10%" }}
                      transition={{ duration: 0.45, delay: (i % 4) * 0.06 }}
                      className="glass rounded-2xl p-5 hover:-translate-y-1 transition-transform duration-300 group relative overflow-hidden"
                    >
                      <div className={`absolute -top-16 -right-16 w-40 h-40 rounded-full blur-3xl opacity-20 bg-gradient-to-tr ${d.color} group-hover:opacity-40 transition-opacity`} />
                      <div className="flex items-start gap-3">
                        <span className="w-7 h-7 rounded-md bg-white/[0.06] border border-white/10 flex items-center justify-center mt-0.5">
                          <Check size={13} />
                        </span>
                        <div>
                          <h3 className="font-semibold">{s.name}</h3>
                          <p className="mt-1 text-sm text-white/55 leading-relaxed">{s.desc}</p>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </section>
          );
        })}
      </div>

      <CTABand
        headline={<>Not sure which division fits? <span className="text-gradient-neon">We&apos;ll map it for you.</span></>}
        sub="45-minute strategy call. We diagnose, prioritize and walk you through the right starting point."
        primary={{ label: "Book Strategy Call", to: "/contact" }}
        secondary={{ label: "See Case Studies", to: "/case-studies" }}
      />
    </div>
  );
}
